import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Loader2,
    Lock,
    Flag,
    Sparkles,
    Lightbulb,
    ArrowRight,
    FileText,
    Download,
    ChevronRight,
} from "lucide-react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    LabelList,
    Tooltip,
} from "recharts";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import { ScoreGauge, scoreBandColor } from "../components/ScoreGauge";

const FINDING_COLUMNS = [
    { key: "strength", label: "Strengths", color: "#2E5D3A" },
    { key: "gap", label: "Gaps", color: "#B8641F" },
    { key: "systems_misalignment", label: "Systems Misalignment", color: "#5B2B7B" },
    { key: "continuous_improvement", label: "Continuous Improvement", color: "#0F7B8A" },
];

const PRIORITY_META = {
    high: { label: "High", className: "bg-red-50 text-red-700 border-red-200" },
    medium: { label: "Medium", className: "bg-amber-50 text-amber-800 border-amber-200" },
    low: { label: "Low", className: "bg-slate-50 text-slate-700 border-slate-200" },
};

function labelize(t) {
    return t
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function DomainDashboardPage() {
    const { cycleId, domainKey } = useParams();
    const navigate = useNavigate();
    const [daId, setDaId] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [evidenceOpen, setEvidenceOpen] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const cycleResp = await api.get(`/cycles/${cycleId}`);
                const match = cycleResp.data.domain_assessments.find(
                    (x) => x.domain?.key === domainKey,
                );
                if (!match) {
                    setError("Domain not found in this cycle");
                    return;
                }
                setDaId(match.id);
                const { data: full } = await api.get(`/domain-assessments/${match.id}/dashboard`);
                setData(full);
                setError(null);
            } catch (e) {
                setError(extractError(e, "Could not load dashboard"));
            } finally {
                setLoading(false);
            }
        })();
    }, [cycleId, domainKey]);

    if (loading) {
        return (
            <div className="text-[#0B2545] flex items-center gap-3">
                <Loader2 className="animate-spin" size={18} /> Loading dashboard…
            </div>
        );
    }
    if (error || !data) {
        return <div className="text-red-700">{error}</div>;
    }

    const { domain, domain_assessment, cycle, indicators, selected_red_flags, auto_red_flags, findings_by_category, recommendations, evidence_summary, teacher_summary } = data;
    const isActive = domain.is_active;
    const isDraft = cycle.status === "draft";

    if (!isActive) {
        return (
            <div className="space-y-4" data-testid="dashboard-inactive">
                <Link to={`/app/cycles/${cycleId}/dashboard`} className="text-xs text-[#1E56A0] hover:underline">
                    ← Back to cycle dashboards
                </Link>
                <div className="rounded-2xl bg-white border border-[#D6E6F2] p-10 text-center">
                    <Sparkles className="mx-auto text-[#1E56A0]" size={28} />
                    <h1 className="mt-3 font-serif text-2xl text-[#0B2545] font-semibold">
                        {domain.name}
                    </h1>
                    <p className="mt-2 text-[#0E1B2C]/70">Coming soon — this domain isn't active in Phase 2/3.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="domain-dashboard-page">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <Link to={`/app/cycles/${cycleId}/dashboard`} className="text-xs text-[#1E56A0] hover:underline">
                    ← Back to cycle dashboards
                </Link>
                {daId && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white"
                        data-testid="download-domain-pdf-btn"
                        onClick={async () => {
                            try {
                                const resp = await api.get(`/domain-assessments/${daId}/report.pdf`, { responseType: "blob" });
                                const cd = resp.headers?.["content-disposition"] || "";
                                const match = /filename="?([^";]+)"?/.exec(cd);
                                const url = URL.createObjectURL(resp.data);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = match?.[1] || `ESSS_DomainReport_${domainKey}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                URL.revokeObjectURL(url);
                            } catch (err) {
                                toast.error(extractError(err, "Download failed"));
                            }
                        }}
                    >
                        <FileText size={12} /> Download PDF
                    </Button>
                )}
            </div>

            {/* Header band */}
            <div className="rounded-2xl overflow-hidden shadow-lg shadow-[#0B2545]/5">
                <div className="px-6 py-6 text-white grid grid-cols-1 lg:grid-cols-12 gap-6 items-center" style={{ backgroundColor: domain.color_hex }}>
                    <div className="lg:col-span-8">
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold opacity-90">
                            Domain dashboard · {cycle.name}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 mt-1">
                            <h1
                                className="font-serif text-3xl sm:text-4xl font-semibold leading-tight"
                                data-testid="domain-name"
                            >
                                {domain.name}
                            </h1>
                            {cycle.status === "locked" && (
                                <Badge className="bg-white/20 border-white/30 text-white border">
                                    <Lock size={12} className="mr-1" /> Locked
                                </Badge>
                            )}
                        </div>
                        <p className="mt-3 text-sm text-white/85 max-w-3xl leading-relaxed">
                            {domain.definition}
                        </p>
                    </div>
                    <div className="lg:col-span-4 flex justify-center lg:justify-end">
                        <div className="bg-white/10 backdrop-blur rounded-2xl p-3 border border-white/15">
                            <ScoreGauge value={domain_assessment.computed_domain_score} color="#FFFFFF" size={220} />
                            <div className="text-center mt-1 text-sm text-white/95">
                                {domain_assessment.rating_descriptor?.label || "Unrated"}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isDraft ? (
                <div className="rounded-2xl border border-dashed border-[#8AC4FF] bg-white p-10 text-center" data-testid="dashboard-draft">
                    <p className="text-[#0B2545] font-semibold">This cycle hasn't started yet.</p>
                    <p className="text-sm text-[#0E1B2C]/70 mt-1">
                        Once a reviewer begins scoring, the dashboard will populate.
                    </p>
                </div>
            ) : (
                <>
                    {/* Teacher rollup for Instructional Core */}
                    {teacher_summary && (
                        <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5 flex flex-wrap items-center justify-between gap-4" data-testid="teacher-summary">
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">
                                    Teacher rollup
                                </div>
                                <div className="mt-1 text-sm text-[#0E1B2C]/80">
                                    {teacher_summary.complete} of {teacher_summary.total} teacher assessments complete
                                </div>
                            </div>
                            <div className="flex items-center gap-6">
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">
                                        Rollup score
                                    </div>
                                    <div className="font-serif text-2xl font-semibold text-[#0B2545]">
                                        {teacher_summary.domain_overall_rollup != null
                                            ? teacher_summary.domain_overall_rollup.toFixed(2)
                                            : "—"}
                                    </div>
                                </div>
                                <Button
                                    variant="outline"
                                    className="rounded-full border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white"
                                    onClick={() => navigate(`/app/cycles/${cycleId}/domains/instructional/teachers`)}
                                    data-testid="open-teachers-btn"
                                >
                                    Manage teachers <ChevronRight size={14} />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Indicator bar chart */}
                    <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5" data-testid="indicator-chart">
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">
                            Indicator scores
                        </div>
                        <div className="mt-4 h-[280px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={indicators.map((i) => ({
                                        name: `${i.position}. ${i.name}`,
                                        score: i.score == null ? 0 : i.score,
                                        raw: i.score,
                                    }))}
                                    layout="vertical"
                                    margin={{ top: 8, right: 24, bottom: 8, left: 200 }}
                                >
                                    <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="#E5EEF7" />
                                    <XAxis type="number" domain={[0, 4]} ticks={[0, 1, 2, 3, 4]} stroke="#0B254588" fontSize={12} />
                                    <YAxis type="category" dataKey="name" width={200} stroke="#0B254588" fontSize={12} interval={0} />
                                    <Tooltip
                                        formatter={(v, _, item) => [item?.payload?.raw == null ? "unrated" : item.payload.raw.toFixed(2), "score"]}
                                        contentStyle={{ borderRadius: 8, border: "1px solid #D6E6F2" }}
                                    />
                                    <Bar dataKey="score" radius={[4, 4, 4, 4]}>
                                        {indicators.map((i, idx) => (
                                            <Cell key={idx} fill={scoreBandColor(i.score)} />
                                        ))}
                                        <LabelList
                                            dataKey="raw"
                                            position="right"
                                            formatter={(v) => (v == null ? "—" : v.toFixed(2))}
                                            fill="#0B2545"
                                            fontSize={11}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Heatmap */}
                    <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5" data-testid="lookfor-heatmap">
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">
                            Look-for heatmap
                        </div>
                        <div className="mt-4 space-y-3">
                            {indicators.map((ind) => (
                                <div key={ind.id} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-center">
                                    <div className="md:col-span-2 text-sm text-[#0B2545] font-semibold">
                                        {ind.position}. {ind.name}
                                    </div>
                                    <div
                                        className="md:col-span-4 grid gap-2"
                                        style={{ gridTemplateColumns: `repeat(${Math.max(ind.look_fors.length, 1)}, minmax(0, 1fr))` }}
                                    >
                                        {ind.look_fors.map((lf) => {
                                            const bg = scoreBandColor(lf.rating);
                                            return (
                                                <div
                                                    key={lf.id}
                                                    className="relative rounded-md p-2 text-white text-xs font-semibold h-14 flex flex-col justify-between overflow-hidden group cursor-help"
                                                    style={{ backgroundColor: bg }}
                                                    title={`${lf.name}${lf.rating != null ? ` · rating ${lf.rating.toFixed(2)}` : " · unrated"}${lf.notes ? `\nNotes: ${lf.notes}` : ""}`}
                                                    data-testid={`heatmap-cell-${lf.id}`}
                                                >
                                                    <div className="truncate text-[10px] opacity-90">
                                                        {ind.position}.{lf.position}
                                                    </div>
                                                    <div className="text-lg font-serif font-bold text-right">
                                                        {lf.rating != null ? lf.rating.toFixed(1) : "—"}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 flex flex-wrap gap-3 text-[11px]">
                            {[
                                ["≤ 1.5", "#DC2626"],
                                ["1.5 – 2.5", "#D97706"],
                                ["2.5 – 3.5", "#2563EB"],
                                ["> 3.5", "#059669"],
                                ["unrated", "#CBD5E1"],
                            ].map(([label, color]) => (
                                <div key={label} className="flex items-center gap-1">
                                    <div className="h-3 w-3 rounded" style={{ backgroundColor: color }} />
                                    <span className="text-[#0E1B2C]/70">{label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Red flags */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="red-flags-panel">
                        <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5">
                            <div className="flex items-center gap-2 text-[#0B2545] mb-3">
                                <Flag size={16} />
                                <h3 className="font-serif font-semibold text-lg">Selected red flags</h3>
                                <span className="text-xs text-[#0E1B2C]/60">({selected_red_flags.length})</span>
                            </div>
                            {selected_red_flags.length === 0 ? (
                                <div className="text-sm text-[#0E1B2C]/60">None selected.</div>
                            ) : (
                                <ul className="space-y-2" data-testid="selected-red-flags-list">
                                    {selected_red_flags.map((rf) => (
                                        <li key={rf.id} className="rounded-lg bg-[#F8FBFF] border border-[#D6E6F2] px-3 py-2 text-sm">
                                            <div className="font-semibold text-[#0B2545]">{rf.text}</div>
                                            {rf.indicates && <div className="text-[#0E1B2C]/70">→ {rf.indicates}</div>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5">
                            <div className="flex items-center gap-2 text-[#0B2545] mb-3">
                                <Sparkles size={16} className="text-[#B8641F]" />
                                <h3 className="font-serif font-semibold text-lg">Auto-detected</h3>
                                <span className="text-xs text-[#0E1B2C]/60">({auto_red_flags.length})</span>
                            </div>
                            {auto_red_flags.length === 0 ? (
                                <div className="text-sm text-[#0E1B2C]/60">No auto-detected issues 🎉</div>
                            ) : (
                                <ul className="space-y-2" data-testid="auto-red-flags-list">
                                    {auto_red_flags.map((rf, i) => (
                                        <li key={i} className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm" data-testid={`auto-flag-${rf.kind}`}>
                                            <div className="font-semibold text-amber-900">{rf.text}</div>
                                            <div className="text-amber-800/70 text-xs">{rf.kind}</div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    {/* Findings */}
                    <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5" data-testid="findings-summary">
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-4">
                            Key findings
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                            {FINDING_COLUMNS.map((col) => {
                                const rows = (findings_by_category[col.key] || []);
                                return (
                                    <div key={col.key} className="rounded-lg border border-[#D6E6F2] bg-[#F8FBFF] overflow-hidden" data-testid={`findings-col-${col.key}`}>
                                        <div className="px-3 py-2 flex items-center gap-2 border-b border-[#D6E6F2]" style={{ color: col.color }}>
                                            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: col.color }} />
                                            <div className="font-semibold text-sm">{col.label}</div>
                                            <div className="ml-auto text-xs text-[#0E1B2C]/60">{rows.length}</div>
                                        </div>
                                        <ul className="p-3 space-y-2 text-sm min-h-[64px]">
                                            {rows.length === 0 ? (
                                                <li className="text-[#0E1B2C]/50 text-xs">None</li>
                                            ) : (
                                                rows.map((r) => (
                                                    <li key={r.id} className="whitespace-pre-wrap">{r.text}</li>
                                                ))
                                            )}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Recommendations */}
                    <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5" data-testid="recommendations-summary">
                        <div className="flex items-center gap-2 text-[#0B2545] mb-3">
                            <Lightbulb size={16} />
                            <h3 className="font-serif font-semibold text-lg">Recommendations</h3>
                            <span className="text-xs text-[#0E1B2C]/60">({recommendations.length})</span>
                        </div>
                        {recommendations.length === 0 ? (
                            <div className="text-sm text-[#0E1B2C]/60">No recommendations yet.</div>
                        ) : (
                            <ul className="space-y-2" data-testid="recommendations-list">
                                {recommendations.map((r) => {
                                    const p = PRIORITY_META[r.priority] || PRIORITY_META.medium;
                                    return (
                                        <li key={r.id} className="rounded-lg bg-[#F8FBFF] border border-[#D6E6F2] px-3 py-2 text-sm flex items-start gap-3" data-testid={`recommendation-summary-${r.id}`}>
                                            <Badge className={p.className + " border shrink-0"} variant="outline">{p.label}</Badge>
                                            <div className="whitespace-pre-wrap">{r.text}</div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    {/* Evidence summary */}
                    <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5" data-testid="evidence-summary">
                        <div className="flex items-center gap-2 text-[#0B2545] mb-3">
                            <FileText size={16} />
                            <h3 className="font-serif font-semibold text-lg">Evidence</h3>
                            <span className="text-xs text-[#0E1B2C]/60">({evidence_summary.total} files)</span>
                            <Dialog open={evidenceOpen} onOpenChange={setEvidenceOpen}>
                                <DialogTrigger asChild>
                                    <button
                                        className="ml-auto text-sm text-[#1E56A0] hover:underline"
                                        disabled={evidence_summary.total === 0}
                                        data-testid="view-all-evidence-btn"
                                    >
                                        View all evidence →
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="evidence-dialog">
                                    <DialogHeader>
                                        <DialogTitle className="font-serif text-2xl text-[#0B2545]">
                                            All evidence — {domain.name}
                                        </DialogTitle>
                                    </DialogHeader>
                                    <ul className="space-y-2">
                                        {evidence_summary.files.map((e) => (
                                            <li key={e.id} className="rounded-lg border border-[#D6E6F2] px-3 py-2 flex items-center justify-between text-sm">
                                                <div className="min-w-0">
                                                    <div className="font-semibold text-[#0B2545] truncate">{e.filename}</div>
                                                    <div className="text-xs text-[#0E1B2C]/60">
                                                        {labelize(e.evidence_type)} · {(e.size_bytes / 1024).toFixed(1)} KB · {new Date(e.uploaded_at).toLocaleDateString()}
                                                    </div>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="rounded-full"
                                                    onClick={async () => {
                                                        try {
                                                            const resp = await api.get(`/evidence/${e.id}/download`, { responseType: "blob" });
                                                            const url = URL.createObjectURL(resp.data);
                                                            const a = document.createElement("a");
                                                            a.href = url;
                                                            a.download = e.filename;
                                                            document.body.appendChild(a);
                                                            a.click();
                                                            a.remove();
                                                            URL.revokeObjectURL(url);
                                                        } catch (err) {
                                                            toast.error(extractError(err, "Download failed"));
                                                        }
                                                    }}
                                                >
                                                    <Download size={12} /> Download
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </DialogContent>
                            </Dialog>
                        </div>
                        {evidence_summary.total === 0 ? (
                            <div className="text-sm text-[#0E1B2C]/60">No evidence uploaded.</div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(evidence_summary.by_type).map(([t, count]) => (
                                    <Badge
                                        key={t}
                                        className="bg-slate-100 text-slate-700 border-slate-200 border"
                                        variant="outline"
                                        data-testid={`evidence-badge-${t}`}
                                    >
                                        {labelize(t)}: {count}
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export function CycleDashboardIndexPage() {
    const { cycleId } = useParams();
    const [cycle, setCycle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/cycles/${cycleId}`);
                setCycle(data);
            } catch (e) {
                setError(extractError(e, "Could not load cycle"));
            } finally {
                setLoading(false);
            }
        })();
    }, [cycleId]);

    if (loading) return <div className="text-[#0B2545] flex items-center gap-3"><Loader2 className="animate-spin" size={18} /> Loading…</div>;
    if (error || !cycle) return <div className="text-red-700">{error}</div>;
    const active = cycle.domain_assessments.filter((d) => d.domain?.is_active);

    return (
        <div className="space-y-6" data-testid="cycle-dashboard-index">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <Link to={`/app/cycles/${cycleId}`} className="text-xs text-[#1E56A0] hover:underline">
                        ← Back to cycle
                    </Link>
                    <h1 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545] leading-tight">
                        Cycle dashboards
                    </h1>
                    <p className="mt-2 text-sm text-[#0E1B2C]/70">
                        {cycle.name} · read-only visualizations of the current scoring state.
                    </p>
                </div>
                <Link
                    to={`/app/cycles/${cycleId}/executive`}
                    className="inline-flex items-center gap-2 rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white text-sm px-4 py-2 transition-colors"
                    data-testid="index-open-executive-btn"
                >
                    Executive summary <ArrowRight size={14} />
                </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="dashboard-domain-grid">
                {active.map((da) => (
                    <Link
                        key={da.id}
                        to={`/app/cycles/${cycleId}/dashboard/${da.domain.key}`}
                        className="rounded-2xl border border-[#D6E6F2] bg-white overflow-hidden hover:shadow-lg hover:shadow-[#0B2545]/5 transition-all"
                        data-testid={`dashboard-domain-${da.domain.key}`}
                    >
                        <div className="h-2" style={{ backgroundColor: da.domain.color_hex }} />
                        <div className="p-5">
                            <div className="text-[11px] uppercase tracking-[0.25em] font-bold" style={{ color: da.domain.color_hex }}>
                                Dashboard
                            </div>
                            <div className="mt-1 font-serif text-xl text-[#0B2545] font-semibold">
                                {da.domain.name}
                            </div>
                            <div className="mt-3 text-sm text-[#0E1B2C]/70">
                                {da.total_look_fors > 0
                                    ? `${da.rated_count}/${da.total_look_fors} rated`
                                    : "—"}
                            </div>
                            {da.computed_domain_score != null && (
                                <div className="mt-1 font-serif text-2xl font-semibold text-[#0B2545]">
                                    {da.computed_domain_score.toFixed(2)}
                                </div>
                            )}
                            <div className="mt-3 inline-flex items-center gap-1 text-sm" style={{ color: da.domain.color_hex }}>
                                Open dashboard <ArrowRight size={14} />
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}
